import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Users, CalendarBlank, Pause, Play, Archive,
  PencilSimple, Trash, FloppyDisk, X, Warning, ArrowsClockwise,
} from '@phosphor-icons/react';
import useAuthStore from '../../store/authStore';
import { useTenantPath } from '../../lib/tenantPath';
import { API, inr, fmtDate, PeriodStatusPill } from './_shared';

import { confirm } from '../../components/ConfirmDialog';
import { notify } from '../../lib/notify';
import { PageSkeleton } from '../../components/Skeletons';
/**
 * Cycle detail page.
 *
 * VP+ can:
 *   - Pause / Resume / Archive the cycle
 *   - Generate next period manually (Phase 3 cron will remove this)
 *   - Edit member list + targets (modal — requires a reason; audit-logged)
 *
 * Members can:
 *   - See their target vs sibling targets (transparency)
 *   - Jump into any open period
 *   - Read past results
 */
export default function CycleDetail() {
  const { cycleId } = useParams();
  const nav = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const isVpPlus = ['Vice President', 'President'].includes(user?.role);
  // iter111 — Stalled leads digest (NSM+ only, excludes RSM)
  const isNsmPlus = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const [cycle, setCycle] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cadenceOpen, setCadenceOpen] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/reviews/cycles/${cycleId}`);
      setCycle(r.data.cycle);
      setPeriods(r.data.periods || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cycleId]);

  const patchCycle = async (updates) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/reviews/cycles/${cycleId}`, updates);
      setCycle(r.data);
    } catch (e) {
      notify(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const genNextPeriod = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/cycles/${cycleId}/generate-next-period`);
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to generate period');
    } finally { setBusy(false); }
  };

  if (loading) return <PageSkeleton />;
  if (!cycle) return <div className="p-6 text-red-600">Cycle not found</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="cycle-detail">
      <button onClick={() => nav(tp('/reviews'))} className="text-sm text-[#6D8E87] hover:text-[#00a69c] inline-flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={14} /> Review Center
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold flex items-center gap-2 flex-wrap">
            <span>{cycle.frequency} · Created by {cycle.created_by_name || 'unknown'}</span>
            {/* iter114 — OVERSEEING chip: shown only when the current
                viewer is the in-charge AND is not a committing member of
                the cycle. Keeps the badge honest (VP+ who is also
                in-charge doesn't need it; a committing NSM who is also
                in-charge sees it because the badge clarifies their
                oversight role). */}
            {cycle.viewer_is_in_charge && !cycle.viewer_is_member && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-[#F0E6D2] text-[#92400E] px-1.5 py-0.5 rounded border border-[#E4CFA1]" data-testid="viewer-overseeing-pill">
                Overseeing
              </span>
            )}
          </div>
          <h1 className="app-page-title truncate" style={{ fontFamily: 'Outfit' }} data-testid="cycle-title">{cycle.title}</h1>
          {cycle.notes && <p className="text-sm text-[#6D8E87] mt-1 max-w-2xl">{cycle.notes}</p>}
          {/* iter114 — Always show who's in-charge (or "no in-charge") as
              a subtle line under the title so the whole team sees clear
              oversight ownership. */}
          <div className="text-xs text-[#3B5A54] mt-2 flex items-center gap-1.5" data-testid="in-charge-display">
            <span className="uppercase tracking-widest text-[10px] font-bold text-[#6D8E87]">In-charge</span>
            {cycle.in_charge_user_id ? (
              <span className="font-semibold text-[#052A23]">
                {cycle.in_charge_user_name}
                <span className="ml-1 text-[#6D8E87] font-normal">— {cycle.in_charge_user_role}</span>
              </span>
            ) : (
              <span className="italic text-[#94A3B8]">no in-charge assigned</span>
            )}
          </div>
        </div>
        {isVpPlus && (
          <div className="flex gap-2 items-center">
            {cycle.status !== 'archived' && (
              <button onClick={() => setCadenceOpen(true)} disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-[#00a69c] text-[#00a69c] rounded-lg hover:bg-[#E2F3F0]"
                data-testid="edit-cadence-btn"
                title="Change frequency, review day, or commitment window. Applies to the NEXT generated period only.">
                <PencilSimple size={12} weight="bold" /> Edit cadence
              </button>
            )}
            {cycle.status === 'active' ? (
              <button onClick={() => patchCycle({ status: 'paused' })} disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50" data-testid="pause-btn">
                <Pause size={12} weight="fill" /> Pause
              </button>
            ) : cycle.status === 'paused' ? (
              <button onClick={() => patchCycle({ status: 'active' })} disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50" data-testid="resume-btn">
                <Play size={12} weight="fill" /> Resume
              </button>
            ) : null}
            {cycle.status !== 'archived' && (
              <button onClick={async () => { if (await confirm({ message: 'Archive this cycle? Historical periods stay visible but no new periods will be generated.' })) patchCycle({ status: 'archived' }); }}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50" data-testid="archive-btn">
                <Archive size={12} /> Archive
              </button>
            )}
          </div>
        )}
      </div>

      {/* Members */}
      <section className="medical-card p-5 mb-6" data-testid="members-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-base font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            <Users size={16} className="inline mr-1.5 text-[#00a69c]" weight="duotone" /> Members & Targets
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#6D8E87]">
              Total target per period: <b className="text-[#00a69c]">{inr((cycle.members || []).reduce((a, m) => a + (m.target_value || 0), 0))}</b>
            </span>
            {isVpPlus && cycle.status !== 'archived' && (
              <button
                onClick={() => setEditOpen(true)}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-[#00a69c] text-[#00a69c] rounded-lg hover:bg-[#E2F3F0]"
                data-testid="edit-members-btn"
              >
                <PencilSimple size={12} weight="bold" /> Edit members & targets
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F9F7]">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Member</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Role</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Target / period</th>
              </tr>
            </thead>
            <tbody>
              {(cycle.members || []).map((m) => (
                <tr key={m.user_id} className={`border-t border-[#D5E8E4] ${m.user_id === user?.id ? 'bg-[#E2F3F0]/40' : ''}`} data-testid={`member-${m.user_id}`}>
                  <td className="px-3 py-2 text-[#052A23] font-medium">{m.user_name} {m.user_id === user?.id && <span className="text-[10px] text-[#00a69c]">(you)</span>}</td>
                  <td className="px-3 py-2 text-xs text-[#6D8E87]">{m.user_role}</td>
                  <td className="px-3 py-2 text-right font-bold text-[#052A23]">{inr(m.target_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* iter111/114 — Stalled leads digest visible to NSM+ globally OR
          the cycle's in-charge (backend gates the endpoint the same way). */}
      {(isNsmPlus || cycle.viewer_can_manage) && <StalledLeadsDigest cycleId={cycleId} tp={tp} />}

      {/* Periods */}
      <section data-testid="periods-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            <CalendarBlank size={20} className="inline mr-1.5 text-[#00a69c]" weight="duotone" /> Periods ({periods.length})
          </h2>
          {isVpPlus && cycle.status === 'active' && (
            <button onClick={genNextPeriod} disabled={busy} className="text-xs font-medium text-[#00a69c] hover:underline" data-testid="gen-period-btn">
              + Generate next period
            </button>
          )}
        </div>
        {periods.length === 0 ? (
          <div className="medical-card p-6 text-center text-sm text-[#6D8E87]">No periods yet.</div>
        ) : (
          <div className="space-y-2">
            {periods.map((p) => (
              <Link
                key={p.id}
                to={p.live_status === 'commitment_open'
                  ? tp(`/reviews/commit/${p.id}`)
                  : tp(`/reviews/periods/${p.id}`)}
                className="medical-card p-4 hover:border-[#00a69c] transition flex items-center gap-4 group"
                data-testid={`period-row-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#052A23]">Review: {fmtDate(p.review_date)}</span>
                    <PeriodStatusPill status={p.live_status} small />
                  </div>
                  <div className="text-xs text-[#6D8E87] mt-0.5">
                    Period: {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                    {p.closed_at && <> · closed {fmtDate(p.closed_at)} by {p.closed_by || 'system'}</>}
                  </div>
                </div>
                <ArrowRight size={16} className="text-[#00a69c] group-hover:translate-x-1 transition" />
              </Link>
            ))}
          </div>
        )}
      </section>

      {editOpen && (
        <EditMembersModal
          cycle={cycle}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => { setCycle(updated); setEditOpen(false); load(); }}
        />
      )}
      {cadenceOpen && (
        <EditCadenceModal
          cycle={cycle}
          onClose={() => setCadenceOpen(false)}
          onSaved={(updated) => { setCycle(updated); setCadenceOpen(false); load(); }}
        />
      )}
    </div>
  );
}


/**
 * Edit-members modal. Add/remove members, adjust ₹ targets. Any change to the
 * membership set OR target values requires a mandatory reason (≥5 chars) which
 * gets logged to audit_logs + review_cycle_changes on the backend.
 */
function EditMembersModal({ cycle, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState(
    (cycle.members || []).map((m) => ({ ...m, target_value: Number(m.target_value) || 0 })),
  );
  // iter114 — track in-charge picker locally. "" = cleared. Backend
  // treats empty as null (removes any existing in-charge).
  const [inChargeId, setInChargeId] = useState(cycle.in_charge_user_id || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/goals/assignable-users`)
      .then((r) => setUsers(r.data.users || r.data || []))
      .catch(() => setUsers([]));
  }, []);

  const originalById = useMemo(() => {
    const m = {};
    (cycle.members || []).forEach((x) => { m[x.user_id] = x; });
    return m;
  }, [cycle.members]);

  const availableUsers = useMemo(() => {
    const picked = new Set(members.map((m) => m.user_id));
    return users.filter((u) => !picked.has(u.id));
  }, [users, members]);

  const addMember = (uid) => {
    const u = users.find((x) => x.id === uid);
    if (!u) return;
    setMembers((ms) => [...ms, { user_id: u.id, user_name: u.name, user_role: u.role, target_value: 0 }]);
  };
  const rmMember = (uid) => setMembers((ms) => ms.filter((m) => m.user_id !== uid));
  const setTarget = (uid, v) => setMembers((ms) => ms.map((m) => (
    m.user_id === uid ? { ...m, target_value: Number(v) || 0 } : m
  )));

  // Diff preview
  const diff = useMemo(() => {
    const currentIds = new Set(members.map((m) => m.user_id));
    const originalIds = new Set(Object.keys(originalById));
    const added = members.filter((m) => !originalIds.has(m.user_id));
    const removed = (cycle.members || []).filter((m) => !currentIds.has(m.user_id));
    const changed = members.filter((m) => {
      const o = originalById[m.user_id];
      if (!o) return false;
      return Number(o.target_value || 0) !== Number(m.target_value || 0);
    });
    return { added, removed, changed };
  }, [members, originalById, cycle.members]);

  const membershipChanged = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0;
  const inChargeChanged = (inChargeId || '') !== (cycle.in_charge_user_id || '');
  const somethingChanged = membershipChanged || inChargeChanged;

  const validTargets = members.every((m) => m.target_value > 0);
  const canSave =
    members.length > 0 &&
    validTargets &&
    somethingChanged &&
    reason.trim().length >= 5;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setError('');
    try {
      const body = {
        members: members.map((m) => ({ user_id: m.user_id, target_value: m.target_value })),
        change_reason: reason.trim(),
      };
      // Only include the in-charge key when it actually changed — this
      // keeps backend PATCH semantics clean (missing key = no update).
      if (inChargeChanged) body.in_charge_user_id = inChargeId || null;
      const r = await axios.patch(`${API}/reviews/cycles/${cycle.id}`, body);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const totalTargets = members.reduce((a, m) => a + (m.target_value || 0), 0);
  const inChargeUser = users.find((u) => u.id === inChargeId);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-members-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2F3F0] sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Edit members &amp; targets</h3>
            <p className="text-xs text-[#6D8E87]">Closed periods stay untouched. Only current/upcoming periods reflect these changes.</p>
          </div>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="edit-members-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#3B5A54]">Members ({members.length})</span>
              <span className="text-xs text-[#6D8E87]">Total <b className="text-[#00a69c]">{inr(totalTargets)}</b></span>
            </div>
            {members.length === 0 && (
              <p className="text-sm text-red-500 italic mb-2">At least one member is required.</p>
            )}
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 mb-2 p-2 bg-[#F4F9F7] rounded-lg" data-testid={`edit-member-row-${m.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#052A23] truncate">{m.user_name}</div>
                  <div className="text-xs text-[#6D8E87]">{m.user_role}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-[#6D8E87]">₹</span>
                  <input
                    type="number" min={0} step={1000}
                    value={m.target_value}
                    onChange={(e) => setTarget(m.user_id, e.target.value)}
                    placeholder="Target"
                    className="w-32 px-2 py-1 border border-[#D5E8E4] rounded text-sm text-right"
                    data-testid={`edit-member-target-${m.user_id}`}
                  />
                </div>
                <button
                  onClick={() => rmMember(m.user_id)}
                  className="w-7 h-7 rounded border border-red-200 text-red-500 hover:bg-red-50 grid place-items-center"
                  data-testid={`edit-member-remove-${m.user_id}`}
                  title="Remove member"
                >
                  <Trash size={12} />
                </button>
              </div>
            ))}

            {availableUsers.length > 0 && (
              <select
                onChange={(e) => { if (e.target.value) { addMember(e.target.value); e.target.value = ''; } }}
                className="w-full mt-2 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
                data-testid="edit-add-member-select"
                value=""
              >
                <option value="">+ Add member…</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
                ))}
              </select>
            )}
          </div>

          {/* iter114 — In-charge picker. Sits directly under the members
              section per the original ask ("same modal as members, new
              In-charges section below the Members list"). Optional and
              singular. */}
          <div className="pt-4 border-t border-[#E2F3F0]">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs font-semibold text-[#3B5A54]">
                In-charge <span className="text-[#6D8E87] font-normal">(optional — deputised for approve + close)</span>
              </span>
              {inChargeId && (
                <button
                  onClick={() => setInChargeId('')}
                  className="text-[10px] uppercase tracking-widest text-red-500 hover:text-red-700 font-bold"
                  data-testid="edit-clear-in-charge-btn"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#6D8E87] mb-2">
              The picked user sees this cycle as <b>Overseeing</b>, can approve under-commits + close periods, and is notified on period rollover / missed deadlines.
            </p>
            <select
              value={inChargeId}
              onChange={(e) => setInChargeId(e.target.value)}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
              data-testid="edit-in-charge-select"
            >
              <option value="">— No in-charge —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
              ))}
            </select>
            {inChargeChanged && (
              <div className="mt-1 text-[11px] text-amber-700" data-testid="edit-in-charge-diff">
                Will change: {cycle.in_charge_user_name || 'none'} → {inChargeUser ? inChargeUser.name : 'none'}
              </div>
            )}
          </div>

          {/* Diff preview */}
          {(membershipChanged || inChargeChanged) && (
            <div className="rounded-lg border border-[#D5E8E4] bg-[#F4F9F7] p-3 text-xs" data-testid="edit-diff-preview">
              <div className="font-semibold text-[#3B5A54] mb-1">Changes to be applied</div>
              {diff.added.length > 0 && (
                <div className="text-emerald-700">
                  + Added: {diff.added.map((m) => `${m.user_name} (${inr(m.target_value)})`).join(', ')}
                </div>
              )}
              {diff.removed.length > 0 && (
                <div className="text-red-600">
                  − Removed: {diff.removed.map((m) => m.user_name).join(', ')}
                </div>
              )}
              {diff.changed.length > 0 && (
                <div className="text-amber-700">
                  ↻ Retargeted: {diff.changed.map((m) => {
                    const o = originalById[m.user_id];
                    return `${m.user_name} (${inr(o?.target_value || 0)} → ${inr(m.target_value)})`;
                  }).join(', ')}
                </div>
              )}
              {inChargeChanged && (
                <div className="text-[#00857e]">
                  ★ In-charge: {cycle.in_charge_user_name || 'none'} → {inChargeUser ? inChargeUser.name : 'none'}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">
              Reason for change * <span className="text-[#6D8E87] font-normal">(min 5 chars — logged for audit)</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Ravi joined the North team on 12-Feb; retargeted Q4 for realistic pipeline."
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm resize-none"
              data-testid="edit-reason-input"
            />
          </div>

          {error && <div className="text-sm text-red-700 border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded" data-testid="edit-error">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E2F3F0] sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
            data-testid="edit-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave || saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="edit-save-btn"
            title={!somethingChanged ? 'No changes yet' : (!validTargets ? 'All targets must be > 0' : (reason.trim().length < 5 ? 'Reason required (min 5 chars)' : ''))}
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Feb 2026 — Edit-cadence modal. VP+ can adjust the review rhythm on an
 * existing cycle. New cadence applies to the NEXT generated period only —
 * the currently-generated upcoming period and all historical ones are
 * untouched. Change requires a reason (≥5 chars) and is audit-logged +
 * appended to review_cycle_changes.
 */
function EditCadenceModal({ cycle, onClose, onSaved }) {
  const [freq, setFreq] = useState(cycle.frequency);
  const [dow, setDow] = useState(cycle.review_day_of_week ?? 4);
  const [dom, setDom] = useState(cycle.review_day_of_month ?? 1);
  const [moq, setMoq] = useState(cycle.review_month_of_quarter ?? 3);
  const [win, setWin] = useState(cycle.commitment_window_hours ?? 24);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const changed = (
    freq !== cycle.frequency
    || (freq === 'weekly' && dow !== (cycle.review_day_of_week ?? 4))
    || (freq !== 'weekly' && dom !== (cycle.review_day_of_month ?? 1))
    || (freq === 'quarterly' && moq !== (cycle.review_month_of_quarter ?? 3))
    || win !== (cycle.commitment_window_hours ?? 24)
  );
  const validReason = reason.trim().length >= 5;

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = { change_reason: reason.trim(), frequency: freq, commitment_window_hours: Number(win) };
      if (freq === 'weekly') payload.review_day_of_week = Number(dow);
      if (freq === 'monthly' || freq === 'quarterly') payload.review_day_of_month = Number(dom);
      if (freq === 'quarterly') payload.review_month_of_quarter = Number(moq);
      const r = await axios.patch(`${API}/reviews/cycles/${cycle.id}`, payload);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update cadence');
    } finally {
      setSaving(false);
    }
  };

  const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" data-testid="edit-cadence-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E2ECEA]">
          <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Edit cadence</h3>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="close-cadence-btn"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="text-[11px] px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900" data-testid="cadence-warning">
            Changes apply to the <b>next period generated</b>. The current upcoming period and all historical periods stay on their original schedule.
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Frequency</label>
            <select value={freq} onChange={(e) => setFreq(e.target.value)}
              className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white"
              data-testid="cadence-frequency-select">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>

          {freq === 'weekly' && (
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-2">Review day of week</label>
              <div className="flex gap-1" data-testid="cadence-dow-picker">
                {DOW_LABELS.map((label, i) => (
                  <button key={i} type="button" onClick={() => setDow(i)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition border ${dow === i ? 'bg-[#00a69c] text-white border-[#00a69c]' : 'bg-white text-[#3B5A54] border-[#B8D5CE] hover:border-[#00a69c]'}`}
                    data-testid={`cadence-dow-${i}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(freq === 'monthly' || freq === 'quarterly') && (
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Review day of month (1–28)</label>
              <input type="number" min="1" max="28" value={dom} onChange={(e) => setDom(e.target.value)}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm"
                data-testid="cadence-dom-input" />
            </div>
          )}

          {freq === 'quarterly' && (
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Review month of quarter</label>
              <select value={moq} onChange={(e) => setMoq(e.target.value)}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white"
                data-testid="cadence-moq-select">
                <option value={1}>1st month</option>
                <option value={2}>2nd month</option>
                <option value={3}>3rd month (end)</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Commitment window (hours before review)</label>
            <input type="number" min="1" max="168" value={win} onChange={(e) => setWin(e.target.value)}
              className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm"
              data-testid="cadence-window-input" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Reason for change (min 5 chars) *</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Shifting review day to Monday for the sales huddle"
              className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm"
              data-testid="cadence-reason-input" />
          </div>

          {error && <p className="text-xs text-rose-600" data-testid="cadence-error">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[#E2ECEA] bg-[#F4F9F7]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[#3B5A54] hover:text-[#052A23]" data-testid="cancel-cadence-btn">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !changed || !validReason}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#00a69c] text-white hover:bg-[#00bfb3] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-cadence-btn"
            title={!changed ? 'No changes yet' : (!validReason ? 'Reason required (min 5 chars)' : '')}>
            <FloppyDisk size={16} weight="bold" /> {saving ? 'Saving…' : 'Save cadence'}
          </button>
        </div>
      </div>
    </div>
  );
}

// iter111 — Stalled leads digest. Fetches the /stalled-leads endpoint on
// mount, hides silently when empty (no clutter for healthy cycles), and
// renders a red-tinted panel listing every quote committed 3+ weeks in a
// row across the whole cycle. Each row deep-links to the latest period
// where the lead was committed (so the manager can drill in).
function StalledLeadsDigest({ cycleId, tp }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/reviews/cycles/${cycleId}/stalled-leads`);
        if (!cancel) setRows(r.data.rows || []);
      } catch (e) {
        if (!cancel) {
          if (e?.response?.status !== 403) setError(e?.message || 'load failed');
          setRows([]);
        }
      }
    })();
    return () => { cancel = true; };
  }, [cycleId]);

  if (rows === null) return null;
  if (error) return null;
  if (rows.length === 0) return null;

  return (
    <section className="mb-6" data-testid="stalled-leads-digest">
      <div className="rounded-xl border-2 border-red-200 bg-red-50/70 overflow-hidden shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between bg-red-100/70 border-b border-red-200">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-red-700" />
            <div>
              <div className="text-sm font-bold text-red-900" style={{ fontFamily: 'Outfit' }}>
                Stalled leads — 3+ weeks in a row
              </div>
              <div className="text-[11px] text-red-800/80">
                {rows.length} quote{rows.length !== 1 ? 's' : ''} recommitted across consecutive weeks. Investigate whether these should be dropped, escalated, or reforecast.
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-red-700/80 font-bold" data-testid="stalled-count">
            {rows.length} STALLED
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/60 text-[10px] uppercase tracking-wide text-red-900/70">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Member</th>
                <th className="text-left px-3 py-2 font-semibold">Customer</th>
                <th className="text-left px-3 py-2 font-semibold">Product</th>
                <th className="text-right px-3 py-2 font-semibold">Value</th>
                <th className="text-left px-3 py-2 font-semibold">Streak</th>
                <th className="text-left px-3 py-2 font-semibold">First → Latest</th>
                <th className="text-left px-3 py-2 font-semibold">Drill in</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.user_id}-${r.quote_id}`} className="border-t border-red-200/60" data-testid={`stalled-row-${r.quote_id}`}>
                  <td className="px-3 py-2">
                    <div className="text-[#052A23] font-semibold">{r.user_name || '—'}</div>
                    <div className="text-[10px] text-[#6D8E87]">{r.user_role}</div>
                  </td>
                  <td className="px-3 py-2 text-[#052A23] max-w-[200px]">
                    <div className="truncate">{r.customer_name || '—'}</div>
                    <div className="text-[10px] text-[#6D8E87]">{r.quotation_number || ''}</div>
                  </td>
                  <td className="px-3 py-2 text-[#3B5A54] max-w-[180px]"><div className="truncate">{r.product_summary}</div></td>
                  <td className="px-3 py-2 text-right font-bold text-[#052A23] whitespace-nowrap">{inr(r.quote_value)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-200 text-red-900 px-2 py-0.5 rounded" data-testid={`stalled-streak-${r.quote_id}`}>
                      <ArrowsClockwise size={9} weight="bold" /> {r.streak} weeks
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-[#3B5A54] whitespace-nowrap">
                    {fmtDate(r.first_committed_date)} <ArrowRight size={9} className="inline" /> {fmtDate(r.last_committed_date)}
                  </td>
                  <td className="px-3 py-2">
                    <Link to={tp(`/reviews/periods/${r.last_committed_period_id}`)}
                      className="text-[11px] text-red-800 font-semibold underline hover:text-red-900"
                      data-testid={`stalled-drill-${r.quote_id}`}>Open period</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}


